import React from 'react';
import classnames from 'classnames';
import { createUseStyles } from 'react-jss';
import mapValues from 'lodash/mapValues';
import { Theme } from '@app/theme';
import {
  ButtonIcon,
  statusColors,
  StatusLabel,
  TooltipText,
  useToast,
} from 'components';
import {
  Session,
  SessionStatus as SessionStatusEnum,
  usePauseSession,
  useResumeSession,
} from '@app/api';
import { CloseSessionModal } from './CloseSessionModal';
import { useBoolModalState } from '@app/core';
import { useTranslation } from 'react-i18next';

const useStatusStyles = createUseStyles((theme: Theme) =>
  mapValues(theme.colorDefinitions, (color) => ({
    backgroundColor: color.std,
    color: theme.colors.white,
  })),
);

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: theme.borderRadius.std,
    padding: theme.marginBase * 2,
    minWidth: 230,
  },
  icons: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase,
  },
  toolTip: {
    width: '100%',
  },
}));

interface Props {
  session: Session;
  className?: string;
}

export const SessionStatus = ({ session, className }: Props) => {
  const classes = useStyles();
  const labelClasses = useStatusStyles();
  const toast = useToast();
  const { t } = useTranslation();

  const [isModalOpened, openModal, closeModal] = useBoolModalState();

  const { mutate: resume } = useResumeSession(session.id, {
    onError: (e) => {
      toast.exception(e);
    },
  });
  const { mutate: pause } = usePauseSession(session.id);

  const allowPlay = session.status === SessionStatusEnum.PAUSED;
  const allowPause = session.status === SessionStatusEnum.LIVE;
  const allowStop =
    session.status === SessionStatusEnum.PAUSED ||
    session.status === SessionStatusEnum.LIVE ||
    session.status === SessionStatusEnum.FULL;
  const color = statusColors[session.status || 'DRAFT'];

  return (
    <>
      <div
        className={classnames(
          classes.container,
          labelClasses[color],
          className,
        )}
      >
        <StatusLabel status={session.status} />

        <div className={classes.icons}>
          {allowPlay && (
            <TooltipText
              title={t('sessions.status.toolTips.PLAY')}
              requireDiv
              placement="top"
            >
              <ButtonIcon
                icon="ri-play-line"
                color="white"
                onClick={() => resume()}
                title="Play"
              />
            </TooltipText>
          )}
          {allowPause && (
            <TooltipText
              title={t('sessions.status.toolTips.PAUSED')}
              requireDiv
              placement="top"
            >
              <ButtonIcon
                icon="ri-pause-line"
                color="white"
                onClick={() => pause()}
                title="Pause"
              />
            </TooltipText>
          )}
          {allowStop && (
            <TooltipText
              title={t('sessions.status.toolTips.STOP')}
              requireDiv
              placement="top"
            >
              <ButtonIcon icon="ri-stop-line" color="ink" onClick={openModal} />
            </TooltipText>
          )}
        </div>
      </div>
      <CloseSessionModal
        isOpen={isModalOpened}
        onRequestClose={closeModal}
        sessionId={session.id}
      />
    </>
  );
};
